import type { Vector4 } from "three";
import { MAX_BLOBS } from "./atmosphere.ts";

/** Distância máxima (m) da câmera até um projetor de sombra-blob; a sombra encolhe nos últimos 8 m. */
export const BLOB_RANGE = 48;
const BLOB_FADE = 8;
/** Projetores no preset Baixo (o resto, `MAX_BLOBS`): só muda o uniform da contagem, nenhum shader. */
export const BLOB_LIMIT_LOW = 8;
/** Quantas sombras-blob o shader desenha no preset (Baixo: metade). */
export const blobLimit = (preset: string): number => (preset === "baixo" ? BLOB_LIMIT_LOW : MAX_BLOBS);

/** Raio da sombra de um jogador (m): os ombros do boneco têm ~0,62 m. */
export const PLAYER_BLOB_RADIUS = 0.45;

/** Um projetor no quadro: pés em (x, y, z), raio da elipse e prioridade (maior entra primeiro). */
export interface BlobCaster {
  id: string;
  x: number;
  y: number;
  z: number;
  radius: number;
  priority: number;
  /** Altura máxima da projeção; 0/ausente usa o estilo global dos atores. */
  lift?: number;
  /** Distância² à câmera no plano (preenchida por `selectBlobs`). */
  d2?: number;
}

const byPriorityThenDistance = (a: BlobCaster, b: BlobCaster) => b.priority - a.priority || a.d2! - b.d2!;

/**
 * Os projetores do quadro que entram no shader: até `range` m da câmera (no plano), por prioridade e
 * depois os mais perto, no máximo `limit` (e nunca mais que `MAX_BLOBS`). `out` é reaproveitado.
 */
export function selectBlobs(casters: readonly BlobCaster[], cameraX: number, cameraZ: number, limit: number, range = BLOB_RANGE, out: BlobCaster[] = []): BlobCaster[] {
  out.length = 0;
  for (const c of casters) {
    c.d2 = (c.x - cameraX) ** 2 + (c.z - cameraZ) ** 2;
    if (c.radius > 0 && c.d2 <= range * range) out.push(c);
  }
  out.sort(byPriorityThenDistance);
  out.length = Math.min(out.length, Math.max(0, Math.min(limit, MAX_BLOBS)));
  return out;
}

/**
 * Sombras-blob (F51): a cada quadro, as camadas dizem quem projeta (`add`) e `end` escolhe os mais
 * perto da câmera e escreve `uBlobs`/`uBlobCount` nos uniforms da atmosfera (os mesmos objetos de
 * todos os materiais). Sem draw call: o terreno, o capim e o chão das vitrines desenham a sombra.
 */
export class BlobShadows {
  private readonly uniforms: { uBlobs: { value: Vector4[] }; uBlobCount: { value: number }; uBlobLift: { value: Float32Array } };
  private readonly pool: BlobCaster[] = [];
  private readonly offered: BlobCaster[] = [];
  private size = 0;
  private readonly chosen: BlobCaster[] = [];

  constructor(uniforms: { uBlobs: { value: Vector4[] }; uBlobCount: { value: number }; uBlobLift: { value: Float32Array } }) {
    this.uniforms = uniforms;
  }

  /** Começa um quadro (sem projetores). */
  begin(): void {
    this.size = 0;
  }

  add(id: string, x: number, y: number, z: number, radius: number, priority = 0, lift = 0): void {
    let c = this.pool[this.size];
    if (!c) this.pool.push((c = { id, x, y, z, radius, priority }));
    c.id = id;
    c.x = x;
    c.y = y;
    c.z = z;
    c.radius = radius;
    c.priority = priority;
    c.lift = lift;
    this.size++;
  }

  /**
   * Fecha o quadro: escolhe e escreve os uniforms. Devolve quantos entraram. Nos últimos 8 m do
   * alcance a sombra encolhe até sumir, sem pipocar na borda.
   */
  end(cameraX: number, cameraZ: number, limit: number): number {
    this.offered.length = 0;
    for (let i = 0; i < this.size; i++) this.offered.push(this.pool[i]!);
    selectBlobs(this.offered, cameraX, cameraZ, limit, BLOB_RANGE, this.chosen);
    this.chosen.forEach((c, i) => {
      const edge = Math.min(1, Math.max(0, (BLOB_RANGE - Math.sqrt(c.d2!)) / BLOB_FADE));
      this.uniforms.uBlobs.value[i]!.set(c.x, c.z, c.radius * edge * edge * (3 - 2 * edge), c.y);
      this.uniforms.uBlobLift.value[i] = c.lift ?? 0;
    });
    this.uniforms.uBlobCount.value = this.chosen.length;
    return this.chosen.length;
  }

  /** Todos os projetores oferecidos no último `end` (sem o corte do preset): o pisoteio do capim. */
  get casters(): readonly BlobCaster[] {
    return this.offered;
  }

  /** Projetores oferecidos e os escolhidos no último quadro (ids). */
  stats(): { offered: number; count: number; ids: string[] } {
    return { offered: this.size, count: this.chosen.length, ids: this.chosen.map((c) => c.id) };
  }
}
